import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { House, Copy, Eye, UserSwitch, Trash, Key, LockKeyOpen, Prohibit, CheckCircle, Bank, UploadSimple, DownloadSimple, MagnifyingGlass } from "@phosphor-icons/react";
import { formatET } from "../lib/dateUtils";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { AdminClientProfileModal } from "../components/admin/AdminClientProfileModal";
import { getHomePathForRole } from "../modules/registry";
import { api, formatApiErrorDetail } from "../lib/api";
import { useEditConfirm } from "../context/EditConfirmContext";

const TIME_ZONE_CHOICES = ["Eastern", "Central", "Mountain", "Pacific", "Alaska", "Hawaii"];
const ROLE_LABEL = { client: "Client", affiliate: "Rep", backoffice: "Back Office", admin: "Admin", manager: "Manager" };
const navItems = [{ to: "/admin/users", label: "Users", icon: <House size={18} weight="duotone" />, testId: "nav-admin-users" }];

export default function AdminUsersPage() {
  const confirmEdit = useEditConfirm();
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", role: "client", temp_password: "" });
  const [created, setCreated] = useState(null);
  const [profileClientId, setProfileClientId] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [resetTarget2, setResetTarget2] = useState(null);
  const [resetEmailChecked, setResetEmailChecked] = useState(false);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [selectedAffiliates, setSelectedAffiliates] = useState([]);
  const [bulkManagerId, setBulkManagerId] = useState("none");
  const [createRepOpen, setCreateRepOpen] = useState(false);
  const [repForm, setRepForm] = useState({ first_name: "", last_name: "", phone: "", email: "", time_zone: "Eastern", manager_id: "none" });
  const [createdRep, setCreatedRep] = useState(null);
  const [paymentPanelUserId, setPaymentPanelUserId] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [uploadLeadsRep, setUploadLeadsRep] = useState(null);
  const [uploadCategory, setUploadCategory] = useState("trust");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [gateBlockReason, setGateBlockReason] = useState(null);
  const [resetLogOpen, setResetLogOpen] = useState(false);
  const [resetLog, setResetLog] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState("");
  const [focusedUserId, setFocusedUserId] = useState(null);
  const isStaffRole = form.role === "backoffice" || form.role === "manager";
  const managers = users.filter((u) => u.role === "manager" && u.account_status !== "revoked");
  const affiliates = users.filter((u) => u.role === "affiliate");
  const defaultManagerId = managers.find((m) => m.user_id === "VP001")?.id || managers[0]?.id || "none";
  const visibleUsers = users.filter((u) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (u.display_name || "").toLowerCase().includes(q) || (u.user_id || "").toLowerCase().includes(q) || (u.client_id || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  });

  const fetchUsers = async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data);
    const params = new URLSearchParams(window.location.search);
    const uploadRepId = params.get("upload_rep_id");
    if (uploadRepId) {
      const rep = data.find((u) => u.id === uploadRepId);
      if (rep) {
        setUploadLeadsRep(rep);
        setUploadCategory(params.get("category") === "funding" ? "funding" : "trust");
      }
      window.history.replaceState({}, "", "/admin/users");
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Build 13 fix (Sep 19 2026) — "Jump to Rep" now lands here with ?focus=<id>; scroll that row
  // into view and highlight it briefly, without a page reload, whether we just navigated in or
  // were already sitting on this page.
  useEffect(() => {
    const focusId = new URLSearchParams(location.search).get("focus");
    if (focusId) setFocusedUserId(focusId);
  }, [location.search]);

  useEffect(() => {
    if (!focusedUserId || users.length === 0) return;
    const el = document.querySelector(`[data-testid="user-row-${focusedUserId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    window.history.replaceState({}, "", "/admin/users");
    const t = setTimeout(() => setFocusedUserId(null), 4000);
    return () => clearTimeout(t);
  }, [focusedUserId, users]);

  const createRep = async (e) => {
    e.preventDefault();
    if (!repForm.phone.trim() || !repForm.email.trim()) {
      toast.error("Phone number and email are both required to create a rep");
      return;
    }
    try {
      const { manager_id, ...rest } = repForm;
      const { data } = await api.post("/admin/users", { ...rest, role: "affiliate", manager_id: manager_id === "none" ? null : manager_id });
      setCreatedRep(data);
      setCreateRepOpen(false);
      setRepForm({ first_name: "", last_name: "", phone: "", email: "", time_zone: "Eastern", manager_id: defaultManagerId });
      fetchUsers();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    }
  };

  const viewPaymentInfo = async (u) => {
    if (paymentPanelUserId === u.id) { setPaymentPanelUserId(null); return; }
    setPaymentPanelUserId(u.id);
    setPaymentInfo(null);
    try {
      const { data } = await api.get(`/admin/managers/${u.id}/payment-info`);
      setPaymentInfo(data);
    } catch (e) {
      setPaymentInfo("none");
    }
  };

  const downloadPaymentPdf = async (u) => {
    try {
      const res = await api.get(`/admin/managers/${u.id}/payment-info/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${u.user_id}_payment_tax_info.pdf`;
      a.click();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "This rep hasn't submitted payment info yet");
    }
  };

  const TEMPLATES = {
    trust: { filename: "personal_consumer_leads_template.csv", header: "First Name,Last Name,Phone Number,Email,Credit Score" },
    funding: { filename: "business_leads_template.csv", header: "Business Name,Owner First Name,Owner Last Name,Phone Number,Email,Time in Business,Monthly Revenue,Business Type/Industry,State" },
  };

  const downloadTemplate = (category) => {
    const t = TEMPLATES[category];
    const blob = new Blob([t.header + "\n"], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = t.filename;
    a.click();
  };

  const submitUploadLeads = async (e, override = false) => {
    if (e) e.preventDefault();
    if (!uploadFile) { toast.error("Choose a CSV file to upload"); return; }
    setUploading(true);
    setUploadResult(null);
    setGateBlockReason(null);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      const { data } = await api.post(`/admin/reps/${uploadLeadsRep.id}/upload-leads?category=${uploadCategory}${override ? "&override=true" : ""}`, fd);
      setUploadResult(data);
      toast.success(data.message);
    } catch (e2) {
      const detail = formatApiErrorDetail(e2.response?.data?.detail);
      if (detail && detail.toLowerCase().includes("replenishment gate")) {
        setGateBlockReason(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setUploading(false);
    }
  };

  const openResetLog = async () => {
    setResetLogOpen(true);
    try {
      const { data } = await api.get("/admin/password-reset-log");
      setResetLog(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        role: form.role,
        ...(isStaffRole ? { email: form.email } : {}),
        ...(form.temp_password.trim() ? { temp_password: form.temp_password.trim() } : {}),
      };
      const { data } = await api.post("/admin/users", payload);
      setCreated(data);
      setAddOpen(false);
      setForm({ first_name: "", last_name: "", email: "", role: "client", temp_password: "" });
      fetchUsers();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    }
  };

  const assignManager = async (userId, managerId) => {
    const u = users.find((x) => x.id === userId);
    if (!(await confirmEdit(`${u?.display_name || "this user"}'s manager assignment`))) return;
    try {
      await api.patch(`/admin/users/${userId}/assign-manager`, { manager_id: managerId });
      toast.success("Manager assignment updated");
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const updateTimeZone = async (userId, time_zone) => {
    const u = users.find((x) => x.id === userId);
    if (!(await confirmEdit(`${u?.display_name || "this user"}'s time zone`))) return;
    try {
      await api.patch(`/admin/users/${userId}/timezone`, { time_zone });
      toast.success("Time zone updated");
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleSelectAffiliate = (id) => {
    setSelectedAffiliates((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    setSelectedAffiliates((prev) => (prev.length === affiliates.length ? [] : affiliates.map((a) => a.id)));
  };

  const applyBulkAssign = async () => {
    if (!(await confirmEdit(`${selectedAffiliates.length} selected rep(s)' manager assignment`))) return;
    try {
      const { data } = await api.patch("/admin/users/bulk-assign-manager", {
        user_ids: selectedAffiliates,
        manager_id: bulkManagerId === "none" ? null : bulkManagerId,
      });
      toast.success(data.message);
      setSelectedAffiliates([]);
      setBulkManagerId("none");
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const copyCreds = () => {
    const idLine = created.email ? `Email: ${created.email}` : `Client ID: ${created.user_id}`;
    navigator.clipboard.writeText(`${idLine}\nTemporary Password: ${created.temp_password}`);
    toast.success("Credentials copied to clipboard");
  };

  const viewAs = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/view-as`);
      window.location.href = getHomePathForRole(u.role, u.unlocked_modules);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteUser = (u) => setDeleteTarget(u);

  const confirmDeleteUser = async () => {
    const u = deleteTarget;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`${u.display_name} was permanently deleted`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      setDeleteTarget(null);
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setDeleteTarget(null);
    }
  };

  const resetPassword = (u) => {
    setResetTarget2(u);
    setResetEmailChecked(!!u.email);
  };

  const submitResetPassword = async () => {
    setResetSubmitting(true);
    try {
      const { data } = await api.post(`/admin/users/${resetTarget2.id}/reset-password`, { send_email: resetEmailChecked });
      setResetResult({ display_name: resetTarget2.display_name, email: resetTarget2.email, ...data });
      setResetTarget2(null);
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setResetSubmitting(false);
    }
  };

  const unlockUser = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/unlock`, {});
      toast.success(data.message);
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleAccountStatus = async (u) => {
    const nextStatus = u.account_status === "revoked" ? "active" : "revoked";
    const isRep = u.role === "affiliate";
    const verb = nextStatus === "revoked" ? (isRep ? "Pause" : "Deactivate") : (isRep ? "Unpause" : "Activate");
    if (!(await confirmEdit(`${u.display_name}'s account status (${verb})`))) return;
    try {
      const { data } = await api.patch(`/admin/users/${u.id}/status`, { account_status: nextStatus });
      toast.success(data.message);
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const [bulkMaintenanceLoading, setBulkMaintenanceLoading] = useState(false);
  const toggleUserMaintenance = async (u, enabled) => {
    if (!(await confirmEdit(`${u.display_name}'s maintenance status`))) return;
    try {
      const { data } = await api.patch(`/admin/users/${u.id}/maintenance`, { enabled });
      toast.success(data.message);
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const bulkSetMaintenance = async (enabled) => {
    if (!(await confirmEdit(enabled ? "every non-admin account (put on maintenance)" : "every account (remove from maintenance)"))) return;
    setBulkMaintenanceLoading(true);
    try {
      const { data } = await api.post("/admin/maintenance/bulk", { enabled });
      toast.success(data.message);
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBulkMaintenanceLoading(false);
    }
  };

  const copyResetPassword = () => {
    navigator.clipboard.writeText(resetResult.temp_password);
    toast.success("Temp password copied to clipboard");
  };

  return (
    <DashboardLayout navItems={navItems} moduleName="User Management" moduleBackPath="/admin">
      <div data-testid="admin-users-page">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-1" title="Every account in the system — clients, reps, managers, and back office — with tools to create, reset, pause, or delete any of them.">Users</h1>
            <p className="text-igc-muted text-sm">Every client, rep, manager and back office account — IDs and temp passwords auto-generated.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Button data-testid="maintenance-remove-all-button" onClick={() => bulkSetMaintenance(false)} disabled={bulkMaintenanceLoading} variant="outline" title="Takes every account off maintenance mode at once, restoring normal login access for everyone." className="border-igc-success/40 text-igc-success hover:bg-igc-success hover:text-black">Remove All from Maintenance</Button>
            <Button data-testid="maintenance-put-all-button" onClick={() => bulkSetMaintenance(true)} disabled={bulkMaintenanceLoading} variant="outline" title="Puts every non-admin account on maintenance mode at once, temporarily blocking their login." className="border-igc-danger/40 text-igc-danger hover:bg-igc-danger hover:text-white">Put All on Maintenance</Button>
            <Button data-testid="password-reset-log-button" onClick={openResetLog} variant="outline" title="Shows a full audit trail of every password reset ever performed, who did it, and when." className="border-igc-gold/40 text-igc-goldText hover:bg-igc-gold hover:text-black">Reset Log</Button>
            <Button data-testid="create-rep-button" onClick={() => { setRepForm({ first_name: "", last_name: "", phone: "", email: "", time_zone: "Eastern", manager_id: defaultManagerId }); setCreateRepOpen(true); }} title="Creates a new sales rep account with an auto-generated referral code and welcome email." className="bg-igc-gold hover:bg-igc-goldHover text-black font-semibold">+ Create Rep</Button>
            <Button data-testid="add-user-button" onClick={() => setAddOpen(true)} variant="outline" title="Creates a new Client, Back Office, or Manager account." className="border-igc-gold/40 text-igc-goldText hover:bg-igc-gold hover:text-black">Add User</Button>
          </div>
        </div>

        {affiliates.length > 0 && (
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <label className="flex items-center gap-2 text-xs text-igc-muted cursor-pointer" title="Selects every rep at once so you can bulk-assign them all to one manager.">
              <Checkbox data-testid="select-all-affiliates-checkbox" checked={selectedAffiliates.length === affiliates.length} onCheckedChange={toggleSelectAll} />
              Select all affiliates
            </label>
            {selectedAffiliates.length > 0 && (
              <div className="flex items-center gap-2 bg-igc-surface border border-igc-gold/20 px-3 py-2" data-testid="bulk-assign-manager-bar">
                <span className="text-igc-cream text-xs" data-testid="bulk-assign-selected-count">{selectedAffiliates.length} selected</span>
                <Select value={bulkManagerId} onValueChange={setBulkManagerId}>
                  <SelectTrigger data-testid="bulk-assign-manager-select" title="Choose which manager every selected rep should report to." className="w-44 h-8 text-xs bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                    <SelectValue placeholder="Assign Manager" />
                  </SelectTrigger>
                  <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                    <SelectItem value="none">Unassigned</SelectItem>
                    {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.display_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button data-testid="bulk-assign-manager-apply-button" onClick={applyBulkAssign} title="Applies the manager assignment above to every selected rep." className="h-8 bg-igc-gold hover:bg-igc-goldHover text-black text-xs">Apply</Button>
                <button data-testid="bulk-assign-clear-selection-button" onClick={() => setSelectedAffiliates([])} title="Deselects every rep without applying anything." className="text-igc-muted text-xs hover:text-igc-goldText">Clear</button>
              </div>
            )}
          </div>
        )}

        <div className="relative mb-4 max-w-sm">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-igc-muted" />
          <Input
            data-testid="admin-users-search-input"
            placeholder="Search by name, ID, or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            title="Search narrows the list below in real time as you type — matches name, rep/client ID, or email."
            className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream pl-8"
          />
        </div>

        <div className="space-y-2">
          {visibleUsers.length === 0 && (
            <p className="text-igc-muted text-sm" data-testid="admin-users-search-empty">No users match "{search}".</p>
          )}
          {visibleUsers.map((u) => (
            <div key={u.id}>
            <div className={`bg-igc-surface border p-4 flex items-center justify-between gap-4 flex-wrap transition-colors ${u.id === focusedUserId ? "border-igc-gold ring-2 ring-igc-gold/60" : "border-igc-gold/15"}`} data-testid={`user-row-${u.id}`}>
              <div>
                <p className="text-igc-cream text-sm flex items-center gap-1.5">
                  {u.role === "client" && (
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${u.online ? "bg-igc-success" : "bg-igc-muted/40"}`}
                      title={u.online ? "Online now" : u.last_seen_at ? `Last seen ${formatET(u.last_seen_at, "MMM d, h:mm a")}` : "Never logged in"}
                      data-testid={`presence-dot-${u.id}`}
                    />
                  )}
                  {u.display_name}
                  {u.display_name !== (u.user_id || u.client_id) && (
                    <span className="text-igc-goldText text-xs"> ({u.user_id || u.client_id || "—"})</span>
                  )}
                  {u.locked && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-igc-danger/15 text-igc-danger uppercase tracking-wide" data-testid={`locked-badge-${u.id}`}>Locked</span>
                  )}
                  {u.account_status === "revoked" && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-igc-muted/15 text-igc-muted uppercase tracking-wide" data-testid={`revoked-badge-${u.id}`}>{u.role === "affiliate" ? "Paused" : "Deactivated"}</span>
                  )}
                  {u.account_status === "pending_approval" && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-igc-goldText/15 text-igc-goldText uppercase tracking-wide" data-testid={`pending-badge-${u.id}`}>Pending</span>
                  )}
                  {u.is_on_maintenance && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/15 text-amber-400 uppercase tracking-wide" data-testid={`maintenance-badge-${u.id}`}>On Maintenance</span>
                  )}
                </p>
                <p className="text-igc-muted text-xs">
                  {u.email || "— profile pending —"}{u.phone ? ` — ${u.phone}` : ""} — {formatET(u.created_at, "MMM d, yyyy")}
                  {u.last_login_at ? ` — Last login ${formatET(u.last_login_at, "MMM d, h:mm a")}` : " — Never logged in"}
                  {u.role === "affiliate" && u.availability_hours && (
                    <span data-testid={`availability-hours-${u.id}`}> — Available: {u.availability_hours}</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {u.role === "affiliate" && (
                  <Checkbox data-testid={`select-affiliate-checkbox-${u.id}`} checked={selectedAffiliates.includes(u.id)} onCheckedChange={() => toggleSelectAffiliate(u.id)} />
                )}
                {u.role === "affiliate" && (
                  <Select value={u.time_zone || "Eastern"} onValueChange={(v) => updateTimeZone(u.id, v)}>
                    <SelectTrigger data-testid={`timezone-select-${u.id}`} title="This rep's local time zone — used to schedule callbacks and availability correctly." className="w-32 h-8 text-xs bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                      {TIME_ZONE_CHOICES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {u.role === "affiliate" && (
                  <Select value={u.manager_id || "none"} onValueChange={(v) => assignManager(u.id, v === "none" ? null : v)}>
                    <SelectTrigger data-testid={`assign-manager-select-${u.id}`} title="Which manager this rep reports to." className="w-40 h-8 text-xs bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                      <SelectValue placeholder="Assign Manager" />
                    </SelectTrigger>
                    <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                      <SelectItem value="none">Unassigned</SelectItem>
                      {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.display_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {["manager", "affiliate"].includes(u.role) && (
                  <button data-testid={`payment-info-toggle-${u.id}`} onClick={() => viewPaymentInfo(u)} className="flex items-center gap-1 text-xs text-igc-muted hover:text-igc-goldText" title="View payment & tax info">
                    <Bank size={14} /> Payment Info
                  </button>
                )}
                {u.role === "affiliate" && (
                  <button data-testid={`upload-leads-button-${u.id}`} onClick={() => { setUploadLeadsRep(u); setUploadCategory("trust"); setUploadFile(null); setUploadResult(null); }} className="flex items-center gap-1 text-xs text-igc-muted hover:text-igc-goldText" title="Upload leads to this rep">
                    <UploadSimple size={14} /> Upload Leads
                  </button>
                )}
                {["client", "affiliate"].includes(u.role) && (
                  <button data-testid={`view-profile-button-${u.id}`} onClick={() => setProfileClientId(u.client_id)} title="Opens this user's full profile — contact info, address, and banking/tax details." className="flex items-center gap-1 text-xs text-igc-muted hover:text-igc-goldText">
                    <Eye size={14} /> View Profile
                  </button>
                )}
                {["client", "affiliate", "manager"].includes(u.role) && (
                  <button data-testid={`view-as-button-${u.id}`} onClick={() => viewAs(u)} className="flex items-center gap-1 text-xs text-igc-muted hover:text-igc-goldText" title="View exactly what this user sees, read-only">
                    <UserSwitch size={14} /> View As
                  </button>
                )}
                <span className="text-[10px] px-2 py-1 tracking-wide bg-igc-gold/10 text-igc-goldText uppercase" data-testid={`user-role-${u.id}`} title="This account's role in the system — determines what dashboard and permissions they get.">{ROLE_LABEL[u.role] || u.role}</span>
                {u.role !== "admin" && (
                  <div className="flex items-center gap-1.5" title="Put this account on maintenance">
                    <Switch
                      data-testid={`maintenance-toggle-${u.id}`}
                      checked={!!u.is_on_maintenance}
                      onCheckedChange={(checked) => toggleUserMaintenance(u, checked)}
                    />
                    <span className="text-[10px] text-igc-muted uppercase tracking-wide">Maintenance</span>
                  </div>
                )}
                {u.role !== "admin" && (
                  <>
                    <button data-testid={`reset-password-button-${u.id}`} onClick={() => resetPassword(u)} className="flex items-center gap-1 text-xs text-igc-muted hover:text-igc-goldText" title="Reset password">
                      <Key size={14} />
                    </button>
                    {u.locked && (
                      <button data-testid={`unlock-user-button-${u.id}`} onClick={() => unlockUser(u)} className="flex items-center gap-1 text-xs text-igc-danger hover:text-igc-danger/70" title="Clears a temporary account freeze caused by too many failed login attempts — has nothing to do with activating or deactivating the account.">
                        <LockKeyOpen size={14} />
                      </button>
                    )}
                    <button
                      data-testid={`toggle-status-button-${u.id}`}
                      onClick={() => toggleAccountStatus(u)}
                      className={`flex items-center gap-1 text-xs ${u.account_status === "revoked" ? "text-igc-success hover:text-igc-success/70" : "text-igc-muted hover:text-igc-goldText"}`}
                      title={u.account_status === "revoked" ? (u.role === "affiliate" ? "Unpause account" : "Activate account") : (u.role === "affiliate" ? "Pause account" : "Deactivate account")}
                    >
                      {u.account_status === "revoked" ? <CheckCircle size={14} /> : <Prohibit size={14} />}
                    </button>
                    <button data-testid={`delete-user-button-${u.id}`} onClick={() => deleteUser(u)} className="flex items-center gap-1 text-xs text-igc-danger hover:text-igc-danger/70" title="Delete user">
                      <Trash size={14} />
                    </button>
                  </>
                )}
              </div>
            </div>
            {paymentPanelUserId === u.id && (
              <div className="bg-igc-surfaceLight border border-igc-gold/20 border-t-0 p-5" data-testid="admin-payment-info-panel">
                {paymentInfo === null && <p className="text-igc-muted text-sm" data-testid="admin-payment-info-loading">Loading payment info…</p>}
                {paymentInfo === "none" && <p className="text-igc-muted text-sm">This user hasn't submitted payment info yet.</p>}
                {paymentInfo && paymentInfo !== "none" && (
                  <div className="space-y-1 text-sm">
                    <p className="text-igc-cream">Legal name: {paymentInfo.legal_name}</p>
                    <p className="text-igc-cream">Bank: {paymentInfo.bank_name} ({paymentInfo.account_type})</p>
                    <p className="text-igc-cream">ACH routing: {paymentInfo.ach_routing_last4}</p>
                    <p className="text-igc-cream">ACH account: {paymentInfo.ach_account_last4}</p>
                    <p className="text-igc-cream">Tax ID ({paymentInfo.tin_type}): {paymentInfo.tin_last4}</p>
                    <p className="text-igc-cream">Tax classification: {paymentInfo.tax_classification}</p>
                    <Button data-testid="admin-download-payment-pdf-button" onClick={() => downloadPaymentPdf(u)} title="Downloads this user's full unmasked banking and tax info as a PDF." className="mt-2 bg-igc-gold hover:bg-igc-goldHover text-black text-xs h-8">Download Unmasked PDF</Button>
                  </div>
                )}
              </div>
            )}
            </div>
          ))}
        </div>

        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="add-user-modal">
            <DialogHeader><DialogTitle className="font-serif">Add User</DialogTitle></DialogHeader>
            <form onSubmit={createUser} className="space-y-3">
              <Input data-testid="add-user-first-name-input" required placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Input data-testid="add-user-last-name-input" required placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="add-user-role-select" className="w-full bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="backoffice">Back Office</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                </SelectContent>
              </Select>
              {isStaffRole ? (
                <Input data-testid="add-user-email-input" type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              ) : (
                <p className="text-igc-muted text-xs" data-testid="add-user-client-id-note">
                  A Client ID (e.g. {(form.first_name[0] || "J").toUpperCase()}{(form.last_name[0] || "S").toUpperCase()}001) will be generated — no email needed. The client completes their own profile on first login.
                </p>
              )}
              <Input data-testid="add-user-temp-password-input" placeholder="Temp Password (optional — leave blank to auto-generate)" value={form.temp_password} onChange={(e) => setForm({ ...form, temp_password: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Button data-testid="add-user-submit-button" type="submit" className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">Create User</Button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={!!created} onOpenChange={() => setCreated(null)}>
          <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="user-created-modal">
            <DialogHeader><DialogTitle className="font-serif">User Created</DialogTitle></DialogHeader>
            {created && (
              <div className="space-y-3">
                <p className="text-igc-muted text-sm">Share these credentials securely — the temporary password won't be shown again.</p>
                <div className="bg-igc-surfaceLight border border-igc-gold/20 p-4 space-y-1" data-testid="user-created-credentials">
                  <p className="text-igc-cream text-sm">{created.email ? "Email" : "Client ID"}: <span className="text-igc-goldText">{created.email || created.user_id}</span></p>
                  <p className="text-igc-cream text-sm">Temp Password: <span className="text-igc-goldText">{created.temp_password}</span></p>
                </div>
                <Button data-testid="copy-user-credentials-button" onClick={copyCreds} className="w-full bg-igc-gold hover:bg-igc-goldHover text-black flex items-center gap-2 justify-center">
                  <Copy size={16} weight="duotone" /> Copy Credentials
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <AdminClientProfileModal clientId={profileClientId} open={!!profileClientId} onOpenChange={() => setProfileClientId(null)} />

        <Dialog open={!!uploadLeadsRep} onOpenChange={(v) => { if (!v) { setUploadLeadsRep(null); setUploadResult(null); setGateBlockReason(null); } }}>
          <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="upload-leads-modal">
            <DialogHeader>
              <DialogTitle className="font-serif">Upload Leads {uploadLeadsRep ? `— ${uploadLeadsRep.display_name} (${uploadLeadsRep.user_id})` : ""}</DialogTitle>
              <DialogDescription className="text-igc-muted">Upload a CSV of leads directly into this rep's folder. Handles large files — 5,000-6,000+ rows is fine.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submitUploadLeads} className="space-y-4">
              <div>
                <p className="text-igc-muted text-xs uppercase tracking-widest mb-1">Lead Category</p>
                <Select value={uploadCategory} onValueChange={setUploadCategory}>
                  <SelectTrigger data-testid="upload-leads-category-select" className="w-full bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                    <SelectItem value="trust">Trust & Asset Protection (Personal/Consumer Leads)</SelectItem>
                    <SelectItem value="funding">Business Funding (Business Leads)</SelectItem>
                  </SelectContent>
                </Select>
                <button type="button" data-testid="download-lead-template-button" onClick={() => downloadTemplate(uploadCategory)} className="flex items-center gap-1 text-xs text-igc-goldText hover:text-igc-gold mt-2">
                  <DownloadSimple size={14} /> Download CSV template for this category
                </button>
              </div>
              <div>
                <p className="text-igc-muted text-xs uppercase tracking-widest mb-1">CSV File</p>
                <Input data-testid="upload-leads-file-input" type="file" accept=".csv" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream file:text-igc-goldText" />
                <p className="text-igc-muted text-xs mt-1">Handles large files — 5,000-6,000+ leads per sheet is fine.</p>
              </div>
              <Button data-testid="upload-leads-submit-button" type="submit" disabled={uploading} className="w-full bg-igc-gold hover:bg-igc-goldHover text-black disabled:opacity-50">
                {uploading ? "Uploading…" : "Upload Leads"}
              </Button>
              {gateBlockReason && (
                <div className="bg-igc-danger/10 border border-igc-danger/30 p-4 text-sm" data-testid="upload-leads-gate-blocked">
                  <p className="text-igc-danger">{gateBlockReason}</p>
                  <Button data-testid="upload-leads-override-button" type="button" disabled={uploading} onClick={() => submitUploadLeads(null, true)} className="mt-2 bg-igc-gold hover:bg-igc-goldHover text-black text-xs h-8">
                    Override & Upload Anyway
                  </Button>
                </div>
              )}
              {uploadResult && (
                <div className="bg-igc-surfaceLight border border-igc-gold/20 p-4 text-sm" data-testid="upload-leads-result">
                  <p className="text-igc-success">{uploadResult.created.toLocaleString()} leads created and added to {uploadLeadsRep?.display_name}'s folder.</p>
                  {uploadResult.error_count > 0 && (
                    <p className="text-igc-goldText mt-1">{uploadResult.error_count} row(s) skipped (missing required fields). {uploadResult.errors.length < uploadResult.error_count ? "First 20 shown below." : ""}</p>
                  )}
                  {uploadResult.errors?.length > 0 && (
                    <ul className="text-igc-muted text-xs mt-2 max-h-32 overflow-y-auto space-y-0.5" data-testid="upload-leads-error-list">
                      {uploadResult.errors.map((err, idx) => <li key={idx}>{err}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={createRepOpen} onOpenChange={setCreateRepOpen}>
          <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="create-rep-modal">
            <DialogHeader><DialogTitle className="font-serif">Create Rep</DialogTitle></DialogHeader>
            <form onSubmit={createRep} className="space-y-3">
              <Input data-testid="create-rep-first-name-input" required placeholder="First name" value={repForm.first_name} onChange={(e) => setRepForm({ ...repForm, first_name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Input data-testid="create-rep-last-name-input" required placeholder="Last name" value={repForm.last_name} onChange={(e) => setRepForm({ ...repForm, last_name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Input data-testid="create-rep-email-input" type="email" required placeholder="Email" value={repForm.email} onChange={(e) => setRepForm({ ...repForm, email: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Input data-testid="create-rep-phone-input" required placeholder="Phone number" value={repForm.phone} onChange={(e) => setRepForm({ ...repForm, phone: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <div>
                <p className="text-igc-muted text-xs uppercase tracking-widest mb-1">Timezone</p>
                <Select value={repForm.time_zone} onValueChange={(v) => setRepForm({ ...repForm, time_zone: v })}>
                  <SelectTrigger data-testid="create-rep-timezone-select" className="w-full bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                    {["Eastern", "Central", "Mountain", "Pacific"].map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-igc-muted text-xs uppercase tracking-widest mb-1">Manager</p>
                <Select value={repForm.manager_id} onValueChange={(v) => setRepForm({ ...repForm, manager_id: v })}>
                  <SelectTrigger data-testid="create-rep-manager-select" className="w-full bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue placeholder="Choose a manager" /></SelectTrigger>
                  <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                    <SelectItem value="none">Unassigned</SelectItem>
                    {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.display_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-igc-muted text-xs" data-testid="create-rep-note">
                A referral code (e.g. {(repForm.first_name[0] || "J").toUpperCase()}{(repForm.last_name[0] || "S").toUpperCase()}001) will be auto-generated. A temp password and welcome email with login instructions will be sent automatically.
              </p>
              <Button data-testid="create-rep-submit-button" type="submit" className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">Create Rep</Button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={!!createdRep} onOpenChange={() => setCreatedRep(null)}>
          <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="rep-created-modal">
            <DialogHeader><DialogTitle className="font-serif">Rep Created</DialogTitle></DialogHeader>
            {createdRep && (
              <div className="space-y-3">
                <p className="text-igc-muted text-sm">A welcome email with login credentials was sent to {createdRep.email}.</p>
                <div className="bg-igc-surfaceLight border border-igc-gold/20 p-4 space-y-1" data-testid="rep-created-credentials">
                  <p className="text-igc-cream text-sm">Referral Code: <span className="text-igc-goldText">{createdRep.user_id}</span></p>
                  <p className="text-igc-cream text-sm">Temp Password: <span className="text-igc-goldText">{createdRep.temp_password}</span></p>
                </div>
                <Button data-testid="rep-created-close-button" onClick={() => setCreatedRep(null)} className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">Done</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={!!resetTarget2} onOpenChange={(v) => { if (!v) setResetTarget2(null); }}>
          <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="reset-password-confirm-modal">
            <DialogHeader><DialogTitle className="font-serif">Reset Password</DialogTitle></DialogHeader>
            {resetTarget2 && (
              <div className="space-y-4">
                <p className="text-igc-muted text-sm">Reset {resetTarget2.display_name}'s password? They'll be required to set a new one on next login.</p>
                <div className="flex items-start gap-3 bg-igc-surfaceLight border border-igc-gold/20 p-4">
                  <Checkbox
                    id="send-reset-email"
                    data-testid="reset-password-email-checkbox"
                    checked={resetEmailChecked}
                    disabled={!resetTarget2.email}
                    onCheckedChange={(v) => setResetEmailChecked(!!v)}
                  />
                  <label htmlFor="send-reset-email" className="text-sm text-igc-cream cursor-pointer">
                    {resetTarget2.email
                      ? <>Email the temp password automatically to <span className="text-igc-goldText">{resetTarget2.email}</span></>
                      : <>No email on file — you'll need to copy and share the temp password manually</>}
                    <p className="text-igc-muted text-xs mt-1">{resetEmailChecked ? "Rep receives it directly. " : ""}Unchecked = it only shows on your screen for you to relay yourself.</p>
                  </label>
                </div>
                <div className="flex gap-3">
                  <Button data-testid="reset-password-cancel-button" variant="outline" onClick={() => setResetTarget2(null)} className="flex-1 border-igc-gold/30 text-igc-cream">Cancel</Button>
                  <Button data-testid="reset-password-confirm-button" onClick={submitResetPassword} disabled={resetSubmitting} className="flex-1 bg-igc-gold hover:bg-igc-goldHover text-black">
                    {resetSubmitting ? "Resetting..." : "Reset Password"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={!!resetResult} onOpenChange={() => setResetResult(null)}>
          <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="reset-password-result-modal">
            <DialogHeader><DialogTitle className="font-serif">Password Reset</DialogTitle></DialogHeader>
            {resetResult && (
              <div className="space-y-3">
                {resetResult.email_sent ? (
                  <p className="text-igc-success text-sm" data-testid="reset-password-emailed-notice">Emailed to {resetResult.email} — they can log in with it right away. Keep this as a backup in case it lands in spam:</p>
                ) : (
                  <p className="text-igc-muted text-sm">Share this securely with {resetResult.display_name} — they'll be required to set a new password on next login.</p>
                )}
                <div className="bg-igc-surfaceLight border border-igc-gold/20 p-4 space-y-1" data-testid="reset-password-credentials">
                  <p className="text-igc-cream text-sm">Temp Password: <span className="text-igc-goldText" data-testid="reset-password-temp-value">{resetResult.temp_password}</span></p>
                </div>
                <Button data-testid="copy-reset-password-button" onClick={copyResetPassword} className="w-full bg-igc-gold hover:bg-igc-goldHover text-black flex items-center gap-2 justify-center">
                  <Copy size={16} weight="duotone" /> Copy Temp Password
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
          <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="delete-user-confirm-modal">
            <DialogHeader><DialogTitle className="font-serif">Delete User</DialogTitle></DialogHeader>
            {deleteTarget && (
              <div className="space-y-4">
                <p className="text-igc-cream text-sm" data-testid="delete-user-confirm-message">
                  Permanently delete {deleteTarget.display_name} ({deleteTarget.user_id || deleteTarget.client_id})? This cannot be undone.
                </p>
                <div className="flex gap-3">
                  <Button data-testid="delete-user-cancel-button" onClick={() => setDeleteTarget(null)} variant="outline" className="flex-1 border-igc-gold/40 text-igc-goldText hover:bg-igc-gold hover:text-black">Cancel</Button>
                  <Button data-testid="delete-user-confirm-button" onClick={confirmDeleteUser} className="flex-1 bg-igc-danger hover:bg-igc-danger/80 text-white">Permanently Delete</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={resetLogOpen} onOpenChange={setResetLogOpen}>
          <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream max-h-[80vh] overflow-y-auto" data-testid="password-reset-log-modal">
            <DialogHeader><DialogTitle className="font-serif">Password Reset Audit Log</DialogTitle></DialogHeader>
            {resetLog.length === 0 && <p className="text-igc-muted text-sm" data-testid="password-reset-log-empty">No password resets logged yet.</p>}
            <div className="space-y-2">
              {resetLog.map((l) => (
                <div key={l.id} className="bg-igc-surfaceLight border border-igc-gold/10 p-3 text-sm" data-testid={`password-reset-log-row-${l.id}`}>
                  <p className="text-igc-cream">{l.admin_name} reset {l.target_display_name} ({l.target_code}) — {l.target_role}</p>
                  <p className="text-igc-muted text-xs">{formatET(l.timestamp, "MMM d, yyyy h:mm a")}{l.custom_password ? " — custom password set" : " — auto-generated password"}</p>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
